import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import { useQueryState } from 'nuqs';

import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import removeNullish from '@/helpers/removeNullish';
import { AnimatePresence } from 'framer-motion';
import { queryStateParser, queryParam } from './queryStateParser';
import PaneWrapper from './PaneWrapper';
import MixPanelTracker from './MixPanelTracker';
import pickContent from './pickContent';

type Pane = {
  key: string;
  content: ReactNode;
  type: string;
  id: string | null;
};

const Panes: React.FC = () => {
  const { creator: creatorFromDashboard, isInDashboard } = useContext(DashboardContext);
  const { creatorSlug: creatorSlugFromUserProfile, isInUserProfile } = useContext(UserProfileContext);
  const [queryState, setQueryState] = useQueryState(queryParam, queryStateParser);

  let creatorSlug: string | null = null;
  let creatorId: string | null = null;
  let creatorNoodlePhoneNumber: string | null = null;

  if (isInDashboard) {
    creatorSlug = creatorFromDashboard?.slug ?? null;
    creatorId = creatorFromDashboard?.id ?? null;
    creatorNoodlePhoneNumber = creatorFromDashboard?.noodlePhoneNumber ?? null;
  } else if (isInUserProfile) {
    creatorSlug = creatorSlugFromUserProfile;
    creatorId = null;
  }

  const handleRemoveByKey = useCallback((key: string): void => {
    setQueryState(prevState => prevState ? prevState.filter(s => s.key !== key) : null);
  }, []);

  const replaceByTypeAndId = useCallback((type: string, id: string): void => {
    setQueryState(prevState => {
      if(prevState) {
        const localPrevState = [...prevState];
        prevState?.forEach((p, index) => {
          if(p?.type === type) {
            localPrevState[index] = {
              ...p,
              id,
            };
          }
        });
        return localPrevState;

      }
      return null;
    });
  }, []);

  const panes = useMemo<Pane[]>(() => (queryState || [])
    .map(({ type, id, key, ...rest }) => {
      const content = pickContent({
        creatorId,
        creatorNoodlePhoneNumber,
        creatorSlug,
        id,
        key,
        onChangeTypeAndId: replaceByTypeAndId,
        onCloseKey: handleRemoveByKey,
        type,
        ...rest,
      });
      if (!content) {
        return null;
      }
      return {
        content,
        id,
        key,
        type,
      };
    })
    .filter(removeNullish),
  [queryState, creatorId, creatorSlug, creatorNoodlePhoneNumber, handleRemoveByKey, replaceByTypeAndId],
  );

  const handleClose = (): void => {
    setQueryState(prevState => {
      if (!prevState || prevState.length === 0) {
        return null;
      }
      return prevState.slice(0, -1);
    });
  };

  return (
    <AnimatePresence>
      {panes.map(({ key, content, type, id }) => (
        <PaneWrapper key={key} onClose={handleClose}>
          {content}
          <MixPanelTracker type={type} id={id} />
        </PaneWrapper>
      ))}
    </AnimatePresence>
  );
};

export default Panes;
